/**
 * Integrations API Endpoints
 * CRUD operations for user integrations, OAuth flows, and provider presets
 */

import { http } from '@/lib/http';
import { getActiveWorkspaceId } from '@/lib/api/workspace-context';

function wsIntegrations(sub = ''): string {
  return `/workspaces/${getActiveWorkspaceId()}/integrations${sub}`;
}

/** Integration status */
export type IntegrationStatus = 'active' | 'expired' | 'error' | 'disconnected';

/** Auth type */
export type IntegrationAuthType = 'oauth2' | 'oauth1' | 'api_key' | 'basic' | 'bearer' | 'custom_header';

/**
 * Stable enum for sync error categorization. Drives branching in UI:
 *   - RATE_LIMITED / TEMPORARY: expected & recoverable, sync auto-resumes
 *   - AUTH_EXPIRED: user must reconnect
 *   - UNKNOWN: catch-all, treat as actionable
 */
export type IntegrationSyncErrorCode =
  | 'RATE_LIMITED'
  | 'AUTH_EXPIRED'
  | 'SCOPE_INSUFFICIENT'
  | 'TEMPORARY'
  | 'UNKNOWN';

/** Integration record (credentials are never exposed) */
export interface Integration {
  id: string;
  workspaceId: string;
  userId: string;
  provider: string;
  name: string;
  authType: IntegrationAuthType;
  scopes: string[] | null;
  baseUrl: string | null;
  providerData: Record<string, unknown> | null;
  syncEnabled: boolean;
  syncSchedule: string;
  lastSyncAt: string | null;
  lastSyncError: string | null;
  status: IntegrationStatus;
  createdAt: string;
  updatedAt: string;
  version: number;
  // Sync error fields — populated when the latest sync attempt failed and
  // cleared on the next successful sync. See IntegrationSyncStatusBadge.
  syncError?: string | null;
  syncErrorCode?: IntegrationSyncErrorCode | null;
  syncErrorAt?: string | null;
}

/** Provider category */
export type ProviderCategory = 'health' | 'productivity' | 'business' | 'developer' | 'custom';

/** Developer portal info for OAuth setup */
export interface DeveloperPortalInfo {
  url: string;
  instructions: string[];
  formFields?: { name: string; value: string }[];
}

/** Provider preset from backend */
export interface ProviderPreset {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  category?: ProviderCategory;
  authType: IntegrationAuthType;
  supportsOAuth: boolean;
  hasServerCredentials: boolean; // true if click-to-auth works without user credentials
  endpoints: string[];
  developerPortal?: DeveloperPortalInfo;
}

/** OAuth configuration for user-provided credentials */
export interface OAuthConfig {
  clientId: string;
  clientSecret: string;
}

/** Create integration request */
export interface CreateIntegrationRequest {
  provider: string;
  name: string;
  authType?: IntegrationAuthType;
  credentials?: Record<string, string>;
  oauthConfig?: OAuthConfig;
  baseUrl?: string;
  syncSchedule?: string;
}

/**
 * List all integrations for the current user
 */
export async function listIntegrations(
  authToken: string | null,
  options?: { provider?: string; status?: string },
) {
  const searchParams: Record<string, string> = {};
  if (options?.provider) searchParams.provider = options.provider;
  if (options?.status) searchParams.status = options.status;

  const response = await http.get<{ items: Integration[] }>(wsIntegrations(), {
    authToken,
    searchParams,
  });
  return response.data.items;
}

/**
 * Create a new integration
 */
export async function createIntegration(
  authToken: string | null,
  data: CreateIntegrationRequest,
) {
  const response = await http.post<Integration>(wsIntegrations(), data, {
    authToken,
    headers: { 'Idempotency-Key': crypto.randomUUID() },
  });
  return response.data;
}

/**
 * Delete (disconnect) an integration.
 *
 * `mode` controls data handling (tokens are always revoked):
 * - `remove` (default): purge all provider-sourced data and delete the row.
 * - `retain`: soft-disable — keep the synced data (read-only), stop syncing.
 */
export async function deleteIntegration(
  authToken: string | null,
  integrationId: string,
  mode: 'remove' | 'retain' = 'remove',
) {
  const query = mode === 'retain' ? '?mode=retain' : '';
  await http.delete(wsIntegrations(`/${integrationId}${query}`), { authToken });
}

/**
 * Designate an account as the primary (write) account for a module.
 * Exactly one account per module can be primary — the backend transactionally
 * clears the others (`setPrimaryForModule`). The route lives under the
 * `/integrations/google` prefix for historical reasons but the handler is
 * provider-agnostic; IntegrationAccountsModal has always called it for
 * non-Google accounts too.
 */
export async function setPrimaryIntegrationForModule(
  authToken: string | null,
  integrationId: string,
  moduleId: string,
) {
  await http.post(
    `/integrations/google/${integrationId}/modules/${moduleId}/set-primary`,
    {},
    { authToken },
  );
}

/**
 * Initiate OAuth authorization flow — returns URL to redirect the user to
 */
export async function authorizeIntegration(
  authToken: string | null,
  integrationId: string,
  redirectUrl: string,
) {
  const response = await http.post<{ authUrl: string }>(
    wsIntegrations(`/${integrationId}/authorize`),
    { redirectUrl },
    { authToken },
  );
  return response.data.authUrl;
}

/**
 * List available provider presets
 */
export async function listProviders(authToken: string | null) {
  const response = await http.get<{ items: ProviderPreset[] }>(
    wsIntegrations('/providers'),
    { authToken },
  );
  return response.data.items;
}

/**
 * Trigger manual sync for an integration
 */
export async function syncIntegration(
  authToken: string | null,
  integrationId: string,
) {
  const response = await http.post<{ status: string }>(
    wsIntegrations(`/${integrationId}/sync`),
    {},
    { authToken },
  );
  return response.data;
}

/**
 * Trigger a FatSecret diary sync for a client's integration (trainer side).
 */
export async function fatsecretSync(
  authToken: string | null,
  integrationId: string,
) {
  const response = await http.post<{ status: string; created: number; updated: number }>(
    wsIntegrations(`/${integrationId}/fatsecret-sync`),
    {},
    { authToken },
  );
  return response.data;
}

/**
 * Check if web search is enabled
 */
export async function getWebSearchStatus(authToken: string | null) {
  const response = await http.get<{ enabled: boolean }>(
    wsIntegrations('/web-search/status'),
    { authToken },
  );
  return response.data.enabled;
}

/**
 * Enable web search
 */
export async function enableWebSearch(authToken: string | null) {
  const response = await http.post<{ enabled: boolean }>(
    wsIntegrations('/web-search/enable'),
    {},
    { authToken },
  );
  return response.data;
}

/**
 * Disable web search
 */
export async function disableWebSearch(authToken: string | null) {
  await http.delete(wsIntegrations('/web-search'), { authToken });
}

// ─────────────────────────────────────────────────────────────────────────────
// Contacts-outbound sync (two-way contacts, provider-agnostic, §A6.2)
// ─────────────────────────────────────────────────────────────────────────────

export type ContactsOutboundAccessLevel = 'READONLY' | 'READWRITE' | 'NONE';

export interface ContactsOutboundState {
  enabled: boolean;
  eligible: boolean;
  isSolo: boolean;
  accessLevel: ContactsOutboundAccessLevel;
  preview: { proposedCreates: number; matches: number } | null;
  /** Present only on the enable POST that fired a one-time backfill push. */
  pushed?: {
    created: number;
    updated: number;
    /** Linked to an existing provider-side contact instead of created (dedup). */
    adopted: number;
    errors: number;
    /** Eligible contacts left unattempted under the per-call cap (re-enable to continue). */
    remaining: number;
  };
}

/**
 * Fetch current contacts-outbound sync state for an integration (any provider).
 * Returns eligibility, access level, and a dry-run preview count.
 */
export async function getContactsOutboundState(
  integrationId: string,
): Promise<ContactsOutboundState> {
  const response = await http.get<ContactsOutboundState>(
    wsIntegrations(`/${integrationId}/contacts-outbound`),
  );
  return response.data;
}

/**
 * Enable or disable outbound contacts sync for an integration (any provider).
 * Enabling requires a solo workspace + READWRITE contacts scope (403/409 otherwise).
 */
export async function setContactsOutboundEnabled(
  integrationId: string,
  enabled: boolean,
): Promise<ContactsOutboundState> {
  const response = await http.post<ContactsOutboundState>(
    wsIntegrations(`/${integrationId}/contacts-outbound`),
    { enabled },
  );
  return response.data;
}

// ─────────────────────────────────────────────────────────────────────────────
// Scope-aware capabilities (ADR 017 D4 — progressive consent)
// ─────────────────────────────────────────────────────────────────────────────

/** The six provider capabilities. Mirrors @xavior/integrations Capability — the
 *  web package can't import that package (boundary), so the union is restated
 *  here; the capabilities endpoint is the contract that keeps them in sync. */
export type IntegrationCapability = 'mail' | 'calendar' | 'contacts' | 'tasks' | 'drive' | 'notes';

/** Three-status model (ADR 017 Decision 3):
 *   - granted: scope present, capability actively syncing
 *   - available: provider supports it, scope not granted — incremental consent possible
 *   - unavailable: no public API / provider hasn't approved the scope (no "Enable") */
export type CapabilityStatus = 'granted' | 'available' | 'unavailable';

export interface IntegrationCapabilities {
  integrationId: string;
  provider: string;
  capabilities: Record<IntegrationCapability, CapabilityStatus>;
  /** EXACT-match contacts write access (two-way sync §8). */
  contactsAccessLevel: 'READONLY' | 'READWRITE';
}

/**
 * Fetch the scope-aware capability statuses for an integration. The single
 * consumer of GET /integrations/:id/capabilities (ADR 017 D4); drives the shared
 * <ScopeUpgradePrompt>. The http client auto-attaches the workspace + auth headers.
 */
export async function getIntegrationCapabilities(
  integrationId: string,
): Promise<IntegrationCapabilities> {
  const response = await http.get<IntegrationCapabilities>(
    wsIntegrations(`/${integrationId}/capabilities`),
  );
  return response.data;
}

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Send a nutrition connect link to a client (trainer side).
 */
export async function sendNutritionConnectLink(
  authToken: string | null,
  workspaceId: string,
  contactId: string,
): Promise<{ sent: boolean }> {
  const response = await http.post<{ sent: boolean }>(
    `/workspaces/${encodeURIComponent(workspaceId)}/clients/${encodeURIComponent(contactId)}/nutrition-connect-link`,
    {},
    { authToken },
  );
  return response.data;
}
